import React, { useState } from "react";
import {
  Box, Flex, Text, Button, Input, Select, VStack, Divider, Textarea,
  Icon, useColorModeValue, FormLabel, FormControl, RadioGroup, Radio, Stack,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import {
  CheckCircle2, UploadCloud, Trash2,
  Users, AlertTriangle,
  Moon, Info, BadgeCheck,
  Clock, Lock, PlaneLanding, Car, TrainFront, Bus,
} from "lucide-react";
import axios from "axios";
import BeatLoader from "react-spinners/BeatLoader";
import countries from "assets/data/countries.json";

const API = process.env.REACT_APP_API;

const EUROPE_CODES = [
  "AL","AD","AT","BY","BE","BA","BG","HR","CY","CZ","DK","EE","FI","FR",
  "DE","GR","HU","IS","IE","LV","LI","LT","LU","MT","MD","MC","ME","NL",
  "MK","NO","PL","PT","RO","RU","SM","RS","SK","SI","ES","SE","CH","UA","GB",
];

function sortedCountries(list) {
  const it  = list.filter(c => c.code === "IT");
  const eu  = list.filter(c => EUROPE_CODES.includes(c.code)).sort((a, b) => a.name.localeCompare(b.name));
  const us  = list.filter(c => c.code === "US");
  const rest = list.filter(c => c.code !== "IT" && c.code !== "US" && !EUROPE_CODES.includes(c.code))
                   .sort((a, b) => a.name.localeCompare(b.name));
  return [...it, ...eu, ...us, ...rest];
}

const orderedCountries = sortedCountries(countries);

// Arrival details — same options/rules as views/reservation/arrival.jsx
const HOURS   = Array.from({ length: 12 }, (_, i) => String(i + 12).padStart(2, "0")); // 12–23
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")); // 00,05,…,55
const METHODS = [
  { value: "flight", label: "Aereo",   icon: PlaneLanding },
  { value: "car",    label: "Auto",    icon: Car },
  { value: "train",  label: "Treno",   icon: TrainFront },
  { value: "bus",    label: "Autobus", icon: Bus },
];

// Codice fiscale is required for Italian nationals and Italian residents
const needsCodiceFiscale = (d) => d.nationality === "IT" || d.country_of_residency === "IT";

// Arrival counts as done once sent, or once it can no longer be sent
const arrivalDone = (a) => !a || a.submitted || a.locked;

const STEP_REQUIRED = {
  1: ["given_name", "surname", "email", "gender"],
  2: ["date_of_birth", "nationality", "country_of_birth", "country_of_residency"],
  3: ["doc_type", "doc_number", "doc_expiry", "doc_town_of_issue"],
};

const STEP_LABELS = {
  1: "Dati Personali",
  2: "Identità",
  3: "Documento",
};

// ─── Stepper ─────────────────────────────────────────────────────────────────
function Stepper({ currentStep, totalSteps }) {
  return (
    <Flex align="flex-start" mb="32px">
      {Array.from({ length: totalSteps }, (_, i) => {
        const step   = i + 1;
        const done   = step < currentStep;
        const active = step === currentStep;
        return (
          <Flex key={step} align="center" flex={step < totalSteps ? 1 : "none"}>
            <Flex direction="column" align="center" minW="64px">
              <Flex
                w="38px" h="38px" borderRadius="full" align="center" justify="center"
                bg={done ? "green.500" : active ? "brand.500" : "gray.100"}
                border="2px solid"
                borderColor={done ? "green.500" : active ? "brand.500" : "gray.200"}
                transition="all 0.2s"
              >
                {done
                  ? <Icon as={CheckCircle2} color="white" boxSize="20px" />
                  : <Text fontSize="14px" fontWeight="700" color={active ? "white" : "gray.600"}>{step}</Text>
                }
              </Flex>
              <Text
                fontSize="13px" fontWeight={active ? "600" : "400"}
                color={done ? "green.500" : active ? "brand.500" : "gray.600"}
                mt="7px" textAlign="center"
              >
                {STEP_LABELS[step]}
              </Text>
            </Flex>
            {step < totalSteps && (
              <Box flex={1} h="2px" bg={done ? "green.400" : "gray.200"} mt="-14px" mx="4px" transition="all 0.2s" />
            )}
          </Flex>
        );
      })}
    </Flex>
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────
function ReservationSidebar({ reservation, guests, selectedGuest, onSelectGuest, arrival, arrivalSelected, onSelectArrival, cardBg, borderClr }) {
  const mutedColor = useColorModeValue("gray.500", "whiteAlpha.600");
  const labelColor = useColorModeValue("gray.600", "whiteAlpha.600");
  const activeBg   = useColorModeValue("brand.50", "whiteAlpha.200");
  const hoverBg    = useColorModeValue("gray.50", "whiteAlpha.100");
  const completeBg = useColorModeValue("green.50", "whiteAlpha.100");
  const avatarBg   = useColorModeValue("gray.100", "whiteAlpha.100");
  const rowBg      = useColorModeValue("gray.50", "whiteAlpha.50");

  const guestName = (g, i) =>
    (g.given_name || g.surname)
      ? `${g.given_name || ""} ${g.surname || ""}`.trim()
      : `Ospite ${i + 1}`;

  return (
    <Box bg={cardBg} borderRadius="16px" border="1px solid" borderColor={borderClr} overflow="hidden">

      {reservation && (
        <>
          <Box px="20px" pt="20px" pb="18px">
            {/* Section label */}
            <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="12px">
              Riepilogo Prenotazione
            </Text>

            {/* Property name */}
            {reservation.property && (
              <Text fontSize="20px" fontWeight="700" color="gray.800" mb="4px" noOfLines={1} lineHeight="1.3">
                {reservation.property}
              </Text>
            )}

            {/* Reservation ID */}
            <Text fontSize="20px" fontWeight="700" color="gray.800" mb="16px">
              Prenotazione: #{reservation.id}
            </Text>

            {/* Check-in — one line */}
            <Box bg={rowBg} borderRadius="8px" px="12px" py="10px" mb="6px">
              <Flex align="center" justify="space-between">
                <Text fontSize="13px" fontWeight="600" color={labelColor}>Check-in</Text>
                <Text fontSize="14px" fontWeight="600">{reservation.check_in}</Text>
              </Flex>
            </Box>

            {/* Check-out — one line */}
            <Box bg={rowBg} borderRadius="8px" px="12px" py="10px" mb="8px">
              <Flex align="center" justify="space-between">
                <Text fontSize="13px" fontWeight="600" color={labelColor}>Check-out</Text>
                <Text fontSize="14px" fontWeight="600">{reservation.check_out}</Text>
              </Flex>
            </Box>

            {/* Nights + Guests — one line */}
            <Flex gap="6px">
              <Flex align="center" gap="6px" flex={1} bg={rowBg} borderRadius="8px" px="10px" py="9px">
                <Icon as={Moon} boxSize="14px" color={mutedColor} />
                <Text fontSize="13px" color={mutedColor}>{reservation.nights} notti</Text>
              </Flex>
              <Flex align="center" gap="6px" flex={1} bg={rowBg} borderRadius="8px" px="10px" py="9px">
                <Icon as={Users} boxSize="14px" color={mutedColor} />
                <Text fontSize="13px" color={mutedColor}>{reservation.total_guests} ospiti</Text>
              </Flex>
            </Flex>
          </Box>

          <Divider borderColor={borderClr} />
        </>
      )}

      {/* Arrival details */}
      {arrival && (
        <>
          <Box px="16px" pt="16px" pb="12px">
            <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="10px">
              Arrivo
            </Text>
            <Flex
              align="center" gap="10px" px="12px" py="10px" borderRadius="10px" cursor="pointer"
              bg={arrivalSelected ? activeBg : arrival.submitted ? completeBg : "transparent"}
              border="1px solid"
              borderColor={arrivalSelected ? "brand.200" : arrival.submitted ? "green.100" : "transparent"}
              _hover={{ bg: arrivalSelected ? activeBg : hoverBg }}
              onClick={onSelectArrival}
              transition="all 0.15s"
            >
              <Flex w="34px" h="34px" borderRadius="full" flexShrink={0} align="center" justify="center"
                bg={arrival.submitted ? "green.100" : avatarBg}>
                {arrival.submitted
                  ? <Icon as={CheckCircle2} color="green.500" boxSize="20px" />
                  : <Icon as={Clock} color="orange.400" boxSize="20px" />
                }
              </Flex>
              <Box flex={1} minW={0}>
                <Text fontSize="14px" fontWeight="600" noOfLines={1} color={arrival.submitted ? "green.700" : "inherit"}>
                  Dettagli di Arrivo
                </Text>
                {arrival.submitted && arrival.arrival_time && (
                  <Text fontSize="12px" color={mutedColor}>{arrival.arrival_time}</Text>
                )}
              </Box>
            </Flex>
          </Box>
          <Divider borderColor={borderClr} />
        </>
      )}

      {/* Guest list */}
      <Box px="16px" py="16px">
        <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="10px">
          Ospiti
        </Text>
        <VStack spacing="6px" align="stretch">
          {guests.map((guest, i) => {
            const isSelected = !arrivalSelected && selectedGuest?.id === guest.id;
            const isDone     = !!guest.completed_at;
            return (
              <Flex
                key={guest.id}
                align="center"
                gap="10px"
                px="12px" py="10px"
                borderRadius="10px"
                cursor="pointer"
                bg={isSelected ? activeBg : isDone ? completeBg : "transparent"}
                border="1px solid"
                borderColor={isSelected ? "brand.200" : isDone ? "green.100" : "transparent"}
                _hover={{ bg: isSelected ? activeBg : hoverBg }}
                onClick={() => onSelectGuest(guest)}
                transition="all 0.15s"
              >
                {/* Status icon — left only, no initials */}
                <Flex w="34px" h="34px" borderRadius="full" flexShrink={0} align="center" justify="center"
                  bg={isDone ? "green.100" : isSelected ? "orange.50" : avatarBg}>
                  {isDone
                    ? <Icon as={CheckCircle2} color="green.500" boxSize="20px" />
                    : <Icon as={AlertTriangle} color="orange.400" boxSize="20px" />
                  }
                </Flex>

                <Box flex={1} minW={0}>
                  <Text fontSize="14px" fontWeight="600" noOfLines={1} color={isDone ? "green.700" : "inherit"}>
                    {guestName(guest, i)}
                  </Text>
                </Box>
              </Flex>
            );
          })}
        </VStack>
      </Box>
    </Box>
  );
}

// ─── Arrival panel ───────────────────────────────────────────────────────────
function ArrivalPanel({ token, pin, arrival, onSaved, cardBg, borderClr }) {
  const mutedClr = useColorModeValue("gray.500", "whiteAlpha.600");

  const [hour, setHour]               = useState(arrival.arrival_time ? arrival.arrival_time.split(":")[0] : "");
  const [minute, setMinute]           = useState(arrival.arrival_time ? arrival.arrival_time.split(":")[1] : "00");
  const [method, setMethod]           = useState(arrival.arrival_method || "");
  const [transportId, setTransportId] = useState(arrival.transport_id || "");
  const [comments, setComments]       = useState(arrival.comments || "");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  const needsTransport = method === "flight" || method === "train";
  const canSubmit = hour !== "" && method !== "" && (!needsTransport || transportId.trim() !== "");

  const handleSave = () => {
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    axios.post(`${API}/document/${token}/arrival`, {
      pin:            pin.toUpperCase(),
      arrival_time:   `${hour}:${minute}`,
      arrival_method: method,
      transport_id:   needsTransport ? transportId.trim() : null,
      comments:       comments || null,
    })
      .then((res) => {
        setSaving(false);
        onSaved(res.data.arrival);
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Invio non riuscito. Riprova.");
        setSaving(false);
      });
  };

  const selectedMethod = METHODS.find(m => m.value === arrival.arrival_method);

  return (
    <Box bg={cardBg} borderRadius="16px" border="1px solid" borderColor={borderClr} p={{ base: "20px", md: "36px" }}>
      <Text fontSize="18px" fontWeight="700" mb="4px" letterSpacing="-0.2px">Dettagli di Arrivo</Text>
      <Text fontSize="14px" color="gray.600" mb="28px">
        Indica l'orario stimato di arrivo e il mezzo di trasporto, così potremo organizzare al meglio il tuo check-in.
      </Text>

      {arrival.locked ? (
        /* Past the cut-off — read-only */
        <Box>
          {arrival.submitted ? (
            <VStack align="stretch" spacing="10px" mb="20px">
              <Flex align="center" gap="8px">
                <Icon as={Clock} color={mutedClr} boxSize="16px" />
                <Text fontSize="15px" fontWeight="600">{arrival.arrival_time}</Text>
              </Flex>
              {selectedMethod && (
                <Flex align="center" gap="8px">
                  <Icon as={selectedMethod.icon} color={mutedClr} boxSize="16px" />
                  <Text fontSize="15px" fontWeight="600">{selectedMethod.label}</Text>
                </Flex>
              )}
              {arrival.transport_id && <Text fontSize="14px">🎫 {arrival.transport_id}</Text>}
              {arrival.comments && <Text fontSize="14px" color="gray.600" fontStyle="italic">"{arrival.comments}"</Text>}
            </VStack>
          ) : (
            <Text fontSize="14px" color="gray.600" lineHeight="1.7" mb="20px">
              Non avendo ricevuto i tuoi dati, ti aspettiamo durante il nostro orario di check-in standard (16:00–20:00).
            </Text>
          )}
          <Box p="14px" bg="orange.50" borderRadius="12px" border="1px solid" borderColor="orange.200">
            <Flex align="center" gap="6px" mb="6px">
              <Icon as={Lock} color="orange.500" boxSize="14px" />
              <Text fontSize="13px" fontWeight="600" color="orange.600">Non è più possibile modificare i dati di arrivo</Text>
            </Flex>
            <Text fontSize="13px" color="gray.600" lineHeight="1.7">Per qualsiasi modifica, contattaci:</Text>
            {arrival.contact_email && <Text fontSize="13px" fontWeight="600" color="gray.700" mt="6px">✉️ {arrival.contact_email}</Text>}
            {arrival.contact_phone && <Text fontSize="13px" fontWeight="600" color="gray.700">📞 {arrival.contact_phone}</Text>}
          </Box>
        </Box>
      ) : (
        <VStack spacing="22px" align="stretch">
          {error && (
            <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="10px" px="16px" py="12px">
              <Text color="red.600" fontSize="14px">{error}</Text>
            </Box>
          )}

          <FormControl>
            <FormLabel fontSize="13px" fontWeight="600" mb="6px" color="gray.700">
              Orario stimato di arrivo a Firenze <Text as="span" color="red.400">*</Text>
            </FormLabel>
            <Flex gap="10px" align="center">
              <Select placeholder="Ora" value={hour} onChange={e => setHour(e.target.value)} fontSize="14px" maxW="120px">
                {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
              </Select>
              <Text fontWeight="700" color="gray.500" fontSize="18px">:</Text>
              <Select value={minute} onChange={e => setMinute(e.target.value)} fontSize="14px" maxW="120px">
                {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
              </Select>
            </Flex>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="13px" fontWeight="600" mb="10px" color="gray.700">
              Come arriverai a Firenze? <Text as="span" color="red.400">*</Text>
            </FormLabel>
            <RadioGroup value={method} onChange={setMethod}>
              <Stack direction={{ base: "column", md: "row" }} spacing="10px">
                {METHODS.map(m => (
                  <Box
                    key={m.value} as="label" flex={1}
                    display="flex" alignItems="center" gap="10px" p="12px 16px" borderRadius="10px"
                    border="1px solid" borderColor={method === m.value ? "brand.500" : borderClr}
                    bg={method === m.value ? "brand.50" : "transparent"}
                    cursor="pointer" transition="all 0.15s"
                  >
                    <Radio value={m.value} colorScheme="brand" />
                    <Icon as={m.icon} boxSize="18px" color={method === m.value ? "brand.500" : mutedClr} />
                    <Text fontSize="14px" fontWeight={method === m.value ? "600" : "400"} color="gray.700">{m.label}</Text>
                  </Box>
                ))}
              </Stack>
            </RadioGroup>
          </FormControl>

          {needsTransport && (
            <FormControl>
              <FormLabel fontSize="13px" fontWeight="600" mb="6px" color="gray.700">
                {method === "flight" ? "Numero del Volo" : "Numero del Treno"} <Text as="span" color="red.400">*</Text>
              </FormLabel>
              <Input
                fontSize="14px" maxLength={20} textTransform="uppercase"
                value={transportId}
                onChange={e => setTransportId(e.target.value.toUpperCase())}
                placeholder={method === "flight" ? "es. FR1234" : "es. IC509"}
              />
            </FormControl>
          )}

          <FormControl>
            <FormLabel fontSize="13px" fontWeight="600" mb="6px" color="gray.700">
              Note Aggiuntive <Text as="span" color={mutedClr} fontWeight="400">(facoltativo)</Text>
            </FormLabel>
            <Textarea
              fontSize="14px" rows={3} resize="none"
              value={comments}
              onChange={e => setComments(e.target.value)}
              placeholder="es. richieste particolari..."
            />
          </FormControl>

          <Flex justify="flex-end" mt="14px">
            <Button colorScheme="brand" px="36px" fontSize="14px" fontWeight="600"
              onClick={handleSave} isLoading={saving} isDisabled={!canSubmit}>
              {arrival.submitted ? "Aggiorna e Continua" : "Salva e Continua"}
            </Button>
          </Flex>
        </VStack>
      )}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DocumentRequest() {
  const { token } = useParams();

  const [pin, setPin]             = useState("");
  const [verified, setVerified]   = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pinError, setPinError]   = useState("");

  const [reservation, setReservation]     = useState(null);
  const [guests, setGuests]               = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestStep, setGuestStep]         = useState(1);
  const [formData, setFormData]           = useState({});
  const [saving, setSaving]               = useState(false);
  const [saveMsg, setSaveMsg]             = useState("");
  const [saveError, setSaveError]         = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  const [arrival, setArrival]                 = useState(null);
  const [arrivalSelected, setArrivalSelected] = useState(false);

  const [allDone, setAllDone]         = useState(false);
  const [linkClosed, setLinkClosed]   = useState(false);

  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState("");

  // Italian comuni as "ROMA (RM)" — suggestions for town of birth / place of issue
  const [comuni, setComuni] = useState([]);

  const cardBg        = useColorModeValue("white", "navy.800");
  const borderClr     = useColorModeValue("gray.200", "whiteAlpha.100");
  const docRowBg      = useColorModeValue("gray.50", "whiteAlpha.50");
  const pageBg        = useColorModeValue("gray.50", "navy.900");
  const uploadHoverBg = useColorModeValue("brand.50", "whiteAlpha.50");

  const totalSteps = (g) => g?.is_main_guest ? 3 : 2;

  const validateStep = (step) => {
    const required = STEP_REQUIRED[step] || [];
    const errors   = {};

    required.forEach(f => {
      if (!formData[f] || String(formData[f]).trim() === "") errors[f] = "Questo campo è obbligatorio";
    });

    // Phone required for all guests on step 1
    if (step === 1) {
      if (!formData.phone_code) errors.phone_code = "Seleziona il prefisso internazionale";
      if (!formData.phone || formData.phone.trim() === "") {
        errors.phone = "Questo campo è obbligatorio";
      }
    }

    if (selectedGuest?.is_main_guest) {
      if (step === 1) {
        // Valid email
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          errors.email = "Inserisci un indirizzo email valido";
        }
        // Phone number: more than 6 digits
        if (formData.phone) {
          const digits = formData.phone.replace(/\D/g, "");
          if (digits.length <= 6) errors.phone = "Il numero di telefono deve avere almeno 7 cifre";
        }
      }
      if (step === 2) {
        // Must be 18+
        if (formData.date_of_birth) {
          const dob   = new Date(formData.date_of_birth);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          if (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate())) age--;
          if (age < 18) errors.date_of_birth = "L'ospite principale deve avere almeno 18 anni";
        }
        // Extended contract fields — main guest only (contract-pdf.md Q6/Q7)
        if (!formData.town_of_birth || String(formData.town_of_birth).trim() === "") {
          errors.town_of_birth = "Questo campo è obbligatorio";
        }
        if (!formData.street_address || String(formData.street_address).trim() === "") {
          errors.street_address = "Questo campo è obbligatorio";
        }
        if (needsCodiceFiscale(formData) && (!formData.codice_fiscale || String(formData.codice_fiscale).trim() === "")) {
          errors.codice_fiscale = "Il codice fiscale è obbligatorio per i cittadini e i residenti italiani";
        }
      }
      if (step === 3) {
        // ID must not be expired
        if (formData.doc_expiry && new Date(formData.doc_expiry) < new Date()) {
          errors.doc_expiry = "Il documento di identità non deve essere scaduto";
        }
      }
    }

    // Police records need the comune for every guest born in Italy
    if (step === 2 && formData.country_of_birth === "IT" && (!formData.town_of_birth || String(formData.town_of_birth).trim() === "")) {
      errors.town_of_birth = "Questo campo è obbligatorio";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleVerify = () => {
    if (!pin.trim()) return;
    setVerifying(true);
    setPinError("");
    axios.post(`${API}/document/verify`, { token, pin: pin.toUpperCase() })
      .then((res) => {
        setReservation(res.data.reservation);
        setGuests(res.data.guests);
        setVerified(true);
        setVerifying(false);
        setLinkClosed(!!res.data.closed);
        setArrival(res.data.arrival);
        const allComplete = res.data.guests.length > 0 && res.data.guests.every(g => !!g.completed_at);
        if (allComplete && arrivalDone(res.data.arrival)) setAllDone(true);
        const primary = res.data.guests.find(g => g.is_main_guest) || res.data.guests[0];
        if (primary) selectGuest(primary);
        // Arrival details come first until they've been sent
        if (!arrivalDone(res.data.arrival)) setArrivalSelected(true);
        axios.get(`${API}/document/comuni`).then(r => setComuni(r.data.comuni)).catch(() => {});
      })
      .catch((err) => {
        setPinError(err.response?.data?.message || "Codice non valido. Riprova.");
        setVerifying(false);
      });
  };

  const selectGuest = (guest) => {
    setArrivalSelected(false);
    setSelectedGuest(guest);
    setFormData({ ...guest });
    setGuestStep(1);
    setSaveMsg("");
    setSaveError("");
    setUploadError("");
    setValidationErrors({});
  };

  const handleChange = (f, value) => {
    setFormData(prev => ({ ...prev, [f]: value }));
    if (validationErrors[f]) {
      setValidationErrors(prev => { const e = { ...prev }; delete e[f]; return e; });
    }
  };

  const saveCurrentStep = () => {
    if (!validateStep(guestStep)) return;
    setSaving(true);
    setSaveMsg("");
    setSaveError("");
    // phone_code and phone are stored separately — don't glue them together
    const payload = { ...formData, pin: pin.toUpperCase(), step: guestStep };
    axios.post(`${API}/document/${token}/guest/${selectedGuest.id}`, payload)
      .then((res) => {
        const updated = res.data.guest;
        const updatedGuests = guests.map(g => g.id === updated.id ? updated : g);
        setGuests(updatedGuests);
        setSelectedGuest(updated);
        setFormData({ ...updated });
        setSaveMsg("Salvato correttamente.");
        setSaving(false);
        setValidationErrors({});
        if (guestStep < totalSteps(selectedGuest)) {
          setGuestStep(s => s + 1);
        } else {
          // Check if all guests are now complete
          const allComplete = updatedGuests.every(g => !!g.completed_at);
          if (allComplete && arrivalDone(arrival)) setAllDone(true);
        }
      })
      .catch((err) => {
        setSaveError(err.response?.data?.message || "Salvataggio non riuscito. Riprova.");
        const fieldErrors = err.response?.data?.errors || {};
        setValidationErrors(Object.fromEntries(Object.entries(fieldErrors).map(([f, msgs]) => [f, msgs[0]])));
        setSaving(false);
      });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("pin", pin.toUpperCase());
    axios.post(`${API}/document/${token}/guest/${selectedGuest.id}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((res) => {
        const updated = { ...selectedGuest, documents: [...(selectedGuest.documents || []), res.data.document] };
        setSelectedGuest(updated);
        setGuests(prev => prev.map(g => g.id === updated.id ? updated : g));
        setFormData(prev => ({ ...prev, documents: updated.documents }));
        setUploading(false);
      })
      .catch((err) => {
        setUploadError(err.response?.data?.message || "Caricamento non riuscito. Riprova.");
        setUploading(false);
      });
  };

  const deleteDocument = (docId) => {
    axios.delete(`${API}/document/${token}/guest/${selectedGuest.id}/document/${docId}`, {
      params: { pin: pin.toUpperCase() },
    }).then(() => {
      const updated = { ...selectedGuest, documents: selectedGuest.documents.filter(d => d.id !== docId) };
      setSelectedGuest(updated);
      setGuests(prev => prev.map(g => g.id === updated.id ? updated : g));
      setFormData(prev => ({ ...prev, documents: updated.documents }));
    }).catch(() => {
      setUploadError("Eliminazione del documento non riuscita. Riprova.");
    });
  };

  const field = (name, label, children) => (
    <FormControl flex={1} isInvalid={!!validationErrors[name]}>
      <FormLabel fontSize="13px" fontWeight="600" mb="6px" color="gray.700">
        {label} <Text as="span" color="red.400">*</Text>
      </FormLabel>
      {children}
      {validationErrors[name] && (
        <Text fontSize="13px" color="red.500" mt="4px">{validationErrors[name]}</Text>
      )}
    </FormControl>
  );

  const inputStyle = (name) => ({
    fontSize: "14px",
    borderColor: validationErrors[name] ? "red.400" : undefined,
    _focus: { borderColor: validationErrors[name] ? "red.400" : "brand.500" },
  });

  // ─── PIN SCREEN ─────────────────────────────────────────────────────────────
  if (!verified) {
    return (
      <Flex justify="center" align="center" minH="calc(100vh - 124px)" px="20px" bg={pageBg}>
        <Box w="100%" maxW="460px" bg={cardBg} borderRadius="20px" p={{ base: "28px", md: "48px" }} boxShadow="0 4px 24px rgba(0,0,0,0.08)">
          <Box mb="28px">
            <Text fontSize="22px" fontWeight="700" mb="10px" letterSpacing="-0.3px">
              Check-in Online
            </Text>
            <Text color="gray.500" fontSize="15px" lineHeight="1.7">
              Benvenuto. Inserisci il codice di accesso di 6 caratteri che hai ricevuto via WhatsApp o tramite la piattaforma di prenotazione per procedere con la verifica della tua identità.
            </Text>
          </Box>
          {pinError && (
            <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="10px" px="16px" py="12px" mb="20px">
              <Text color="red.600" fontSize="14px">{pinError}</Text>
            </Box>
          )}
          <FormControl mb="24px">
            <FormLabel fontSize="13px" fontWeight="600" mb="8px">Codice di Accesso</FormLabel>
            <Input
              value={pin}
              onChange={e => setPin(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && handleVerify()}
              placeholder="A3F9K2"
              textTransform="uppercase"
              letterSpacing="8px"
              fontSize="22px"
              textAlign="center"
              maxLength={6}
              size="lg"
              borderRadius="10px"
              fontFamily="'Noto Sans', sans-serif"
            />
          </FormControl>
          <Button w="100%" colorScheme="brand" size="lg" borderRadius="10px"
            onClick={handleVerify} isLoading={verifying} isDisabled={pin.length < 6}
            fontSize="15px" fontWeight="600">
            Continua
          </Button>
        </Box>
      </Flex>
    );
  }

  // ─── CLOSED SCREEN (link closed by admin) ───────────────────────────────────
  if (linkClosed) {
    return (
      <Flex justify="center" align="center" minH="calc(100vh - 124px)" px="20px" bg={pageBg}>
        <Box
          w="100%" maxW="520px" bg={cardBg} borderRadius="24px"
          p={{ base: "36px", md: "56px" }} boxShadow="0 4px 32px rgba(0,0,0,0.08)" textAlign="center"
        >
          <Flex justify="center" mb="28px">
            <Flex w="88px" h="88px" borderRadius="full" bg="green.50" align="center" justify="center">
              <Icon as={BadgeCheck} boxSize="52px" color="green.500" />
            </Flex>
          </Flex>
          <Text fontSize="24px" fontWeight="800" letterSpacing="-0.4px" mb="12px" color="gray.800">
            Invio Ricevuto
          </Text>
          <Text fontSize="15px" color="gray.600" lineHeight="1.8" mb="28px">
            I tuoi documenti e i dati degli ospiti sono stati ricevuti e verificati dal nostro team.
            Non è richiesta alcuna ulteriore azione da parte tua.
          </Text>
          <Divider mb="28px" />
          {reservation && (
            <Box mb="28px">
              {reservation.property && (
                <Text fontSize="17px" fontWeight="700" color="gray.800" mb="2px">{reservation.property}</Text>
              )}
              <Text fontSize="14px" color="gray.500" mb="16px">Prenotazione #{reservation.id}</Text>
              <Flex justify="center" gap="20px" flexWrap="wrap">
                <Box textAlign="center">
                  <Text fontSize="12px" color="gray.500" fontWeight="600" textTransform="uppercase" letterSpacing="0.06em" mb="2px">Check-in</Text>
                  <Text fontSize="15px" fontWeight="600" color="gray.700">{reservation.check_in}</Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="12px" color="gray.500" fontWeight="600" textTransform="uppercase" letterSpacing="0.06em" mb="2px">Check-out</Text>
                  <Text fontSize="15px" fontWeight="600" color="gray.700">{reservation.check_out}</Text>
                </Box>
              </Flex>
            </Box>
          )}
          <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="12px" px="20px" py="14px">
            <Text fontSize="14px" color="green.700" lineHeight="1.7">
              Grazie per la collaborazione. Non vediamo l'ora di accoglierti.
            </Text>
          </Box>
        </Box>
      </Flex>
    );
  }

  // ─── MAIN LAYOUT ────────────────────────────────────────────────────────────
  return (
    <Box px={{ base: "16px", md: "40px" }} py="36px" maxW="1200px" mx="auto">
      {allDone ? (
        <Box
          bg={cardBg} border="1px solid" borderColor="green.200"
          borderRadius="16px" px={{ base: "20px", md: "32px" }} py="24px" mb="28px"
          boxShadow="0 2px 12px rgba(72,187,120,0.10)"
        >
          <Flex gap="16px" align="flex-start">
            <Flex w="52px" h="52px" borderRadius="full" bg="green.50" align="center" justify="center" flexShrink={0}>
              <Icon as={BadgeCheck} boxSize="30px" color="green.500" />
            </Flex>
            <Box>
              <Text fontSize="18px" fontWeight="700" color="gray.800" mb="4px">Invio Completato</Text>
              <Text fontSize="14px" color="gray.600" lineHeight="1.7">
                Tutti i dati degli ospiti sono stati inviati correttamente. Il nostro team li verificherà prima del tuo arrivo
                e ti contatterà se servisse altro. Puoi comunque aggiornare qui sotto le informazioni, se necessario.
              </Text>
            </Box>
          </Flex>
        </Box>
      ) : (
        <Box mb="28px">
          <Text fontSize="22px" fontWeight="700" letterSpacing="-0.3px">Check-in Online</Text>
          <Text color="gray.500" fontSize="15px" mt="6px" lineHeight="1.6">
            Indica i dettagli del tuo arrivo e compila i dati per ogni ospite. Tutte le informazioni sono conservate in modo sicuro e utilizzate solo per il check-in.
          </Text>
        </Box>
      )}

      <Flex gap="24px" align="flex-start" flexDirection={{ base: "column", md: "row" }}>

        {/* Sidebar */}
        <Box w={{ base: "100%", md: "300px" }} flexShrink={0}>
          <ReservationSidebar
            reservation={reservation}
            guests={guests}
            selectedGuest={selectedGuest}
            onSelectGuest={selectGuest}
            arrival={arrival}
            arrivalSelected={arrivalSelected}
            onSelectArrival={() => setArrivalSelected(true)}
            cardBg={cardBg}
            borderClr={borderClr}
          />
        </Box>

        {/* Form */}
        <Box flex={1} minW={0}>
          {arrivalSelected && arrival ? (
            <ArrivalPanel
              token={token}
              pin={pin}
              arrival={arrival}
              cardBg={cardBg}
              borderClr={borderClr}
              onSaved={(updated) => {
                setArrival(updated);
                if (guests.length > 0 && guests.every(g => !!g.completed_at)) setAllDone(true);
                // Move on to the first guest still missing data
                const next = guests.find(g => !g.completed_at) || guests.find(g => g.is_main_guest) || guests[0];
                if (next) selectGuest(next);
              }}
            />
          ) : !selectedGuest ? (
            <Flex justify="center" align="center" h="200px" bg={cardBg} borderRadius="16px" border="1px solid" borderColor={borderClr}>
              <Text color="gray.600" fontSize="15px">Seleziona un ospite dall'elenco</Text>
            </Flex>
          ) : (
            <Box bg={cardBg} borderRadius="16px" border="1px solid" borderColor={borderClr} p={{ base: "20px", md: "36px" }}>
              <Text fontSize="18px" fontWeight="700" mb="4px" letterSpacing="-0.2px">
                {(formData.given_name || formData.surname)
                  ? `${formData.given_name || ""} ${formData.surname || ""}`.trim()
                  : "Dati Ospite"}
              </Text>
              <Text fontSize="14px" color="gray.600" mb="28px">
                {selectedGuest.is_main_guest ? "Ospite Principale" : "Ospite Aggiuntivo"}
              </Text>

              {selectedGuest.is_main_guest && (
                <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="10px" px="16px" py="12px" mb="24px">
                  <Flex gap="10px" align="flex-start">
                    <Icon as={Info} color="blue.500" boxSize="18px" mt="1px" flexShrink={0} />
                    <Box>
                      <Text fontSize="13px" fontWeight="700" color="blue.700" mb="3px">Ospite Principale della Prenotazione</Text>
                      <Text fontSize="13px" color="blue.600" lineHeight="1.6">
                        Questa persona è il capogruppo della prenotazione e deve avere più di 18 anni.
                        Solo per questo tipo di ospite è obbligatorio inserire un documento di identità valido.
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              )}

              <Stepper currentStep={guestStep} totalSteps={totalSteps(selectedGuest)} />

              {saveMsg && (
                <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="10px" px="16px" py="12px" mb="20px">
                  <Text color="green.700" fontSize="14px">{saveMsg}</Text>
                </Box>
              )}
              {saveError && (
                <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="10px" px="16px" py="12px" mb="20px">
                  <Text color="red.600" fontSize="14px">{saveError}</Text>
                </Box>
              )}

              {/* Step 1 — Personal Info */}
              {guestStep === 1 && (
                <VStack spacing="18px" align="stretch">
                  <Flex gap="16px" flexDirection={{ base: "column", md: "row" }}>
                    {field("given_name", "Nome", <Input {...inputStyle("given_name")} value={formData.given_name || ""} onChange={e => handleChange("given_name", e.target.value)} />)}
                    {field("surname", "Cognome", <Input {...inputStyle("surname")} value={formData.surname || ""} onChange={e => handleChange("surname", e.target.value)} />)}
                  </Flex>
                  <Flex gap="16px" flexDirection={{ base: "column", md: "row" }}>
                    {field("email", "Indirizzo Email", <Input type="email" {...inputStyle("email")} value={formData.email || ""} onChange={e => handleChange("email", e.target.value)} />)}
                    <FormControl flex={1} isInvalid={!!(validationErrors.phone_code || validationErrors.phone)}>
                      <FormLabel fontSize="13px" fontWeight="600" mb="6px" color="gray.700">
                        Numero di Telefono <Text as="span" color="red.400">*</Text>
                      </FormLabel>
                      <Flex gap="8px">
                        <Select
                          w="140px" flexShrink={0} fontSize="14px"
                          value={formData.phone_code || ""}
                          onChange={e => handleChange("phone_code", e.target.value)}
                          placeholder="Prefisso"
                          borderColor={validationErrors.phone_code ? "red.400" : undefined}
                        >
                          {orderedCountries.map(c => (
                            <option key={c.code} value={c.dial_code}>{c.dial_code}</option>
                          ))}
                        </Select>
                        <Input
                          flex={1} fontSize="14px" type="tel"
                          placeholder="123456789"
                          value={formData.phone || ""}
                          onChange={e => handleChange("phone", e.target.value)}
                          borderColor={validationErrors.phone ? "red.400" : undefined}
                          _focus={{ borderColor: validationErrors.phone ? "red.400" : "brand.500" }}
                        />
                      </Flex>
                      {(validationErrors.phone_code || validationErrors.phone) && (
                        <Text fontSize="13px" color="red.500" mt="4px">
                          {validationErrors.phone_code || validationErrors.phone}
                        </Text>
                      )}
                    </FormControl>
                  </Flex>
                  {field("gender", "Sesso",
                    <Select {...inputStyle("gender")} value={formData.gender || ""} onChange={e => handleChange("gender", e.target.value)} placeholder="Seleziona sesso">
                      <option value="Male">Maschio</option>
                      <option value="Female">Femmina</option>
                      <option value="Other">Altro</option>
                    </Select>
                  )}
                </VStack>
              )}

              {/* Step 2 — Identity */}
              {guestStep === 2 && (
                <VStack spacing="18px" align="stretch">
                  <Flex gap="16px" flexDirection={{ base: "column", md: "row" }}>
                    {field("date_of_birth", "Data di Nascita", <Input type="date" {...inputStyle("date_of_birth")} value={formData.date_of_birth || ""} onChange={e => handleChange("date_of_birth", e.target.value)} />)}
                    {field("nationality", "Nazionalità",
                      <Select {...inputStyle("nationality")} value={formData.nationality || ""} onChange={e => handleChange("nationality", e.target.value)} placeholder="Seleziona stato">
                        {orderedCountries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                      </Select>
                    )}
                  </Flex>
                  <Flex gap="16px" flexDirection={{ base: "column", md: "row" }}>
                    {field("country_of_birth", "Stato di Nascita",
                      <Select {...inputStyle("country_of_birth")} value={formData.country_of_birth || ""} onChange={e => handleChange("country_of_birth", e.target.value)} placeholder="Seleziona stato">
                        {orderedCountries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                      </Select>
                    )}
                    {field("country_of_residency", "Stato di Residenza",
                      <Select {...inputStyle("country_of_residency")} value={formData.country_of_residency || ""} onChange={e => handleChange("country_of_residency", e.target.value)} placeholder="Seleziona stato">
                        {orderedCountries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                      </Select>
                    )}
                  </Flex>

                  {/* Extended contract fields — main guest only (contract-pdf.md Q6/Q7) */}
                  {selectedGuest.is_main_guest && (
                    <>
                      <Flex gap="16px" flexDirection={{ base: "column", md: "row" }}>
                        {field("town_of_birth", "Comune di Nascita", <Input {...inputStyle("town_of_birth")} list={formData.country_of_birth === "IT" ? "comuni-list" : undefined} value={formData.town_of_birth || ""} onChange={e => handleChange("town_of_birth", e.target.value)} />)}
                        {field("street_address", "Indirizzo di Residenza", <Input {...inputStyle("street_address")} value={formData.street_address || ""} onChange={e => handleChange("street_address", e.target.value)} />)}
                      </Flex>
                      <FormControl isInvalid={!!validationErrors.codice_fiscale}>
                        <FormLabel fontSize="13px" fontWeight="600" mb="6px" color="gray.700">
                          Codice Fiscale
                          {needsCodiceFiscale(formData) && <Text as="span" color="red.400"> *</Text>}
                        </FormLabel>
                        <Input
                          {...inputStyle("codice_fiscale")}
                          value={formData.codice_fiscale || ""}
                          maxLength={16}
                          onChange={e => handleChange("codice_fiscale", e.target.value.toUpperCase())}
                          placeholder={needsCodiceFiscale(formData) ? "Obbligatorio per cittadini e residenti italiani" : "Facoltativo"}
                        />
                        {validationErrors.codice_fiscale && (
                          <Text fontSize="13px" color="red.500" mt="4px">{validationErrors.codice_fiscale}</Text>
                        )}
                      </FormControl>
                    </>
                  )}

                  {/* Other guests born in Italy still need a comune for the police record */}
                  {!selectedGuest.is_main_guest && formData.country_of_birth === "IT" && (
                    field("town_of_birth", "Comune di Nascita", <Input {...inputStyle("town_of_birth")} list="comuni-list" placeholder="es. ROMA (RM)" value={formData.town_of_birth || ""} onChange={e => handleChange("town_of_birth", e.target.value)} />)
                  )}

                  <datalist id="comuni-list">
                    {comuni.map(c => <option key={c} value={c} />)}
                  </datalist>
                </VStack>
              )}

              {/* Step 3 — Document (main guest only) */}
              {guestStep === 3 && selectedGuest.is_main_guest && (
                <VStack spacing="18px" align="stretch">
                  <Flex gap="16px" flexDirection={{ base: "column", md: "row" }}>
                    {field("doc_type", "Tipo di Documento",
                      <Select {...inputStyle("doc_type")} value={formData.doc_type || ""} onChange={e => handleChange("doc_type", e.target.value)} placeholder="Seleziona tipo">
                        <option value="identity_card">Carta d'Identità</option>
                        <option value="passport">Passaporto</option>
                        <option value="driving_license">Patente di Guida</option>
                        <option value="interior_ministry_card">Tessera Ministero dell'Interno</option>
                      </Select>
                    )}
                    {field("doc_number", "Numero Documento", <Input {...inputStyle("doc_number")} value={formData.doc_number || ""} onChange={e => handleChange("doc_number", e.target.value)} />)}
                  </Flex>
                  <Flex gap="16px" flexDirection={{ base: "column", md: "row" }}>
                    {field("doc_expiry", "Data di Scadenza", <Input type="date" {...inputStyle("doc_expiry")} value={formData.doc_expiry || ""} onChange={e => handleChange("doc_expiry", e.target.value)} />)}
                    {field("doc_town_of_issue", "Luogo di Rilascio", <Input {...inputStyle("doc_town_of_issue")} list="comuni-issue-list" placeholder="Comune italiano o stato di rilascio" value={formData.doc_town_of_issue || ""} onChange={e => handleChange("doc_town_of_issue", e.target.value)} />)}
                    <datalist id="comuni-issue-list">
                      {formData.nationality === "IT" && comuni.map(c => <option key={c} value={c} />)}
                    </datalist>
                  </Flex>

                  <Divider />

                  <Box>
                    <Text fontWeight="600" fontSize="15px" mb="4px">Carica il Documento di Identità</Text>
                    <Text fontSize="13px" color="gray.500" mb="14px" lineHeight="1.6">
                      Carica una scansione o una foto del tuo documento di identità (JPG, PNG o PDF, max 10MB).
                    </Text>

                    {uploadError && (
                      <Text color="red.500" fontSize="13px" mb="10px">{uploadError}</Text>
                    )}

                    {(formData.documents || []).length > 0 && (
                      <VStack align="stretch" spacing="6px" mb="14px">
                        {(formData.documents || []).map(doc => (
                          <Flex key={doc.id} align="center" gap="10px" p="12px" bg={docRowBg} borderRadius="10px">
                            <Icon as={CheckCircle2} color="green.500" boxSize="16px" flexShrink={0} />
                            <Text fontSize="14px" flex={1} noOfLines={1}>{doc.original_name}</Text>
                            <Button size="xs" variant="ghost" colorScheme="red" flexShrink={0} onClick={() => deleteDocument(doc.id)}>
                              <Trash2 />
                            </Button>
                          </Flex>
                        ))}
                      </VStack>
                    )}

                    {/* Security note */}
                    <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="10px" px="14px" py="10px" mb="14px">
                      <Text fontSize="13px" color="orange.700" lineHeight="1.6">
                        Per motivi di sicurezza e privacy non è possibile visualizzare qui i documenti caricati.
                        L'accesso è riservato esclusivamente al personale autorizzato.
                      </Text>
                    </Box>

                    <Box
                      border="2px dashed" borderColor={borderClr} borderRadius="12px"
                      p="32px" textAlign="center" cursor="pointer"
                      onClick={() => document.getElementById("doc-upload-input").click()}
                      _hover={{ borderColor: "brand.400", bg: uploadHoverBg }}
                      transition="all 0.2s"
                    >
                      {uploading ? <BeatLoader size={8} color="#422CFA" /> : (
                        <>
                          <Icon as={UploadCloud} boxSize="38px" color="gray.300" mb="10px" />
                          <Text fontSize="14px" color="gray.500" fontWeight="600">Clicca per caricare</Text>
                          <Text fontSize="13px" color="gray.600" mt="3px">JPG, PNG, PDF — max 10MB</Text>
                        </>
                      )}
                    </Box>
                    <input id="doc-upload-input" type="file" style={{ display: "none" }} onChange={handleFileUpload} accept="image/*,.pdf" />
                  </Box>
                </VStack>
              )}

              {/* Navigation */}
              <Flex mt="36px" justify="space-between" align="center">
                <Button
                  variant="ghost" colorScheme="gray" fontSize="14px"
                  onClick={() => { setGuestStep(s => s - 1); setSaveMsg(""); setSaveError(""); setValidationErrors({}); }}
                  isDisabled={guestStep === 1}
                >
                  Indietro
                </Button>
                <Button colorScheme="brand" px="36px" fontSize="14px" fontWeight="600" onClick={saveCurrentStep} isLoading={saving}>
                  {guestStep === totalSteps(selectedGuest) ? "Salva e Termina" : "Salva e Continua"}
                </Button>
              </Flex>
            </Box>
          )}
        </Box>
      </Flex>
    </Box>
  );
}
